import React from 'react';
import Hero from '../components/Hero';
import SetupCard from '../components/SetupCard';
import { CURATED_SETUPS, SETUP_BENEFITS } from '../utils/setups';
import { ArrowRight, Star, Shield, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Hero />
      
      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose AudioRoom?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make it easy to find the perfect Hi-Fi setup for your needs and budget
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Curation</h3>
              <p className="text-gray-600">
                Our audio experts carefully select and test every product to ensure quality and value
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-accent-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Best Prices</h3>
              <p className="text-gray-600">
                We track prices across multiple vendors 24/7 to ensure you get the best deals
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Trusted Vendors</h3>
              <p className="text-gray-600">
                All our partner vendors are verified and trusted for quality service and support
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Curated Setups Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Curated Hi-Fi Setups
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From your first turntable to reference-grade systems, we've assembled the perfect setups for every level
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {CURATED_SETUPS.map((setup) => {
              const benefits = SETUP_BENEFITS.find(b => b.level === setup.level)?.benefits || [];
              return (
                <SetupCard
                  key={setup.id}
                  setup={setup}
                  benefits={benefits}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Shop by Category
            </h2>
            <p className="text-xl text-gray-600">
              Explore our carefully curated selection of Hi-Fi components
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Bookshelf Speakers',
                description: 'Compact speakers that deliver exceptional sound quality for any room size',
                path: '/category/bookshelf_speakers',
                image: '/images/speakers-category.jpg'
              },
              {
                title: 'Turntables',
                description: 'From entry-level to audiophile, find the perfect turntable for your vinyl collection',
                path: '/category/turntables',
                image: '/images/turntables-category.jpg'
              },
              {
                title: 'Amplifiers',
                description: 'Power your speakers with our selection of integrated and stereo amplifiers',
                path: '/category/amplifiers',
                image: '/images/amplifiers-category.jpg'
              }
            ].map((category) => (
              <Link
                key={category.path}
                to={category.path}
                className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden card-hover"
              >
                <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                  <div className="w-full h-48 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <span className="text-primary-600 text-lg font-medium">{category.title}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  <div className="flex items-center text-primary-600 font-medium">
                    Explore Products
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Your Hi-Fi Journey?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Browse our complete collection of Hi-Fi products and find your perfect setup today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/category/turntables"
              className="inline-flex items-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors shadow-lg"
            >
              Shop Turntables
            </Link>
            <Link
              to="/category/bookshelf_speakers"
              className="inline-flex items-center px-8 py-4 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-xl transition-colors border-2 border-white/20"
            >
              Shop Speakers
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;